import { HttpClient, robotoHeaders } from "@/shared/http";

import type {
  CreateFeatureFlagRequest,
  FeatureFlagRecord,
  UpdateFeatureFlagRequest,
} from "./FeatureFlagRecord";

export class FeatureFlagService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getAll(
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<FeatureFlagRecord[]> {
    const url = this.#httpClient.constructUrl("v1/feature_flags");
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<FeatureFlagRecord[]>();
  }

  public async create(
    request: CreateFeatureFlagRequest,
  ): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl("v1/feature_flags");
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(request),
    });
    return response.json<FeatureFlagRecord>();
  }

  public async update(
    featureFlagId: string,
    request: UpdateFeatureFlagRequest,
  ): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/feature_flags/${featureFlagId}`,
    );
    const response = await this.#httpClient.put(url, {
      body: JSON.stringify(request),
    });
    return response.json<FeatureFlagRecord>();
  }

  public async delete(featureFlagId: string): Promise<void> {
    const url = this.#httpClient.constructUrl(
      `v1/feature_flags/${featureFlagId}`,
    );
    const response = await this.#httpClient.delete(url);
    return response.throwIfError();
  }

  public async getAllDeleted(
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<FeatureFlagRecord[]> {
    const url = this.#httpClient.constructUrl("v1/feature_flags/deleted");
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<FeatureFlagRecord[]>();
  }

  public async restore(featureFlagId: string): Promise<FeatureFlagRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/feature_flags/${featureFlagId}/restore`,
    );
    const response = await this.#httpClient.post(url, {});
    return response.json<FeatureFlagRecord>();
  }

  public async checkAllFeatureFlags(
    orgId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<Record<string, boolean>> {
    const url = this.#httpClient.constructUrl("v1/feature_flags/check");
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    return response.json<Record<string, boolean>>();
  }

  public async checkFeatureFlag(
    name: string,
    orgId: string,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<boolean> {
    const url = this.#httpClient.constructUrl(
      `v1/feature_flags/check/${encodeURIComponent(name)}`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
    });
    const result = await response.json<{ enabled: boolean }>();
    return result.enabled;
  }
}
